import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { fetchMyShop, updateShop } from '@/store/slices/shopsSlice';
import { Shop } from '@/types';
import Button from '@/components/ui/Button';
import { 
  BuildingStorefrontIcon,
  PencilIcon,
  EyeIcon,
  EyeSlashIcon,
  StarIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { shopsApi, locationsApi, apiClient } from '@/services/api';
import { FRONTEND_BASE_URL } from '@/utils/constants';
import toast from 'react-hot-toast';
import Input from '@/components/ui/Input';
import { getImageUrl } from '@/utils/imageUtils';

const ShopManagementPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentShop, isLoading, error } = useSelector((state: RootState) => state.shops);
  
  const [isEditing, setIsEditing] = useState(false);
  const [serverLink, setServerLink] = useState<string | null>(null);
  const [shopLink, setShopLink] = useState<string | null>(null);
  const [isLoadingLink, setIsLoadingLink] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_public: false,
    location_id: '',
    latitude: '',
    longitude: '',
    business_email: '',
    business_phone: '',
    business_address: '',
    business_website: '',
    business_hours: '',
  });
  
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [locationFormData, setLocationFormData] = useState({
    country_id: '',
    state_id: '',
    district_id: '',
    area_id: '',
  });

  // Branding state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  useEffect(() => {
    dispatch(fetchMyShop());
    
    // Fetch countries for location dropdown
    const fetchCountries = async () => {
      try {
        setIsLoadingLocations(true);
        const response = await locationsApi.getAll('country');
        setCountries(response.data || []);
      } catch (error: any) {
        console.error('Error fetching countries:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    };
    
    fetchCountries();
  }, [dispatch]);

  useEffect(() => {
    const fetchShopLinks = async () => {
      if (currentShop && currentShop.is_public) {
        setIsLoadingLink(true);
        try {
          const response = await shopsApi.getMyShopLink();
          if (response.data?.shop_url) {
            // Server link (API link from backend)
            setServerLink(response.data.shop_url);
            
            // Frontend link (what buyers should use)
            const slug = currentShop.slug || currentShop.id.toString();
            const frontendUrl = `${FRONTEND_BASE_URL}/shop/${slug}`;
            setShopLink(frontendUrl);
          }
        } catch (error) {
          console.error('Failed to fetch shop link:', error);
        } finally {
          setIsLoadingLink(false);
        }
      }
    };

    fetchShopLinks();
  }, [currentShop]);

  useEffect(() => {
    if (currentShop) {
      setFormData({
        name: currentShop.name || '',
        description: currentShop.description || '',
        is_public: currentShop.is_public || false,
        location_id: currentShop.location_id?.toString() || '',
        latitude: currentShop.latitude?.toString() || '',
        longitude: currentShop.longitude?.toString() || '',
        business_email: currentShop.business_email || '',
        business_phone: currentShop.business_phone || '',
        business_address: currentShop.business_address || '',
        business_website: currentShop.business_website || '',
        business_hours: currentShop.business_hours || '',
      });

      // If shop has location_id, fetch the location hierarchy to populate dropdowns
      if (currentShop.location_id) {
        fetchLocationHierarchy(currentShop.location_id);
      }
    }
  }, [currentShop]);
  
  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (locationFormData.country_id) {
        try {
          setIsLoadingLocations(true);
          const response = await locationsApi.getChildren(parseInt(locationFormData.country_id));
          setStates(response.data || []);
          // Reset child selections
          setLocationFormData(prev => ({ ...prev, state_id: '', district_id: '', area_id: '' }));
          setDistricts([]);
          setAreas([]);
        } catch (error: any) {
          console.error('Error fetching states:', error);
        } finally {
          setIsLoadingLocations(false);
        }
      } else {
        setStates([]);
        setDistricts([]);
        setAreas([]);
      }
    };
    
    fetchStates();
  }, [locationFormData.country_id]);
  
  // Fetch districts when state changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (locationFormData.state_id) {
        try {
          setIsLoadingLocations(true);
          const response = await locationsApi.getChildren(parseInt(locationFormData.state_id));
          setDistricts(response.data || []);
          // Reset child selections
          setLocationFormData(prev => ({ ...prev, district_id: '', area_id: '' }));
          setAreas([]);
        } catch (error: any) {
          console.error('Error fetching districts:', error);
        } finally {
          setIsLoadingLocations(false);
        }
      } else {
        setDistricts([]);
        setAreas([]);
      }
    };
    
    fetchDistricts();
  }, [locationFormData.state_id]);
  
  // Fetch areas when district changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (locationFormData.district_id) {
        try {
          setIsLoadingLocations(true);
          const response = await locationsApi.getChildren(parseInt(locationFormData.district_id));
          setAreas(response.data || []);
          // Reset area selection
          setLocationFormData(prev => ({ ...prev, area_id: '' }));
        } catch (error: any) {
          console.error('Error fetching areas:', error);
        } finally {
          setIsLoadingLocations(false);
        }
      } else {
        setAreas([]);
      }
    };
    
    fetchAreas();
  }, [locationFormData.district_id]);
  
  const fetchLocationHierarchy = async (locationId: number) => {
    try {
      // Get location details and traverse up the hierarchy
      const locationResponse = await locationsApi.getById(locationId);
      const location = locationResponse.data;
      
      if (location) {
        // Set the final location_id
        setFormData(prev => ({ ...prev, location_id: locationId.toString() }));
        
        // Traverse up the hierarchy to populate dropdowns
        let currentLoc = location;
        const hierarchy: any = { area_id: '', district_id: '', state_id: '', country_id: '' };
        
        // Build hierarchy from bottom up
        while (currentLoc) {
          if (currentLoc.location_type === 'area') {
            hierarchy.area_id = currentLoc.id.toString();
            if (currentLoc.parent_id) {
              const parentResponse = await locationsApi.getById(currentLoc.parent_id);
              currentLoc = parentResponse.data;
            } else break;
          } else if (currentLoc.location_type === 'district') {
            hierarchy.district_id = currentLoc.id.toString();
            if (currentLoc.parent_id) {
              const parentResponse = await locationsApi.getById(currentLoc.parent_id);
              currentLoc = parentResponse.data;
            } else break;
          } else if (currentLoc.location_type === 'city') {
            // City is between state and district, skip for now
            if (currentLoc.parent_id) {
              const parentResponse = await locationsApi.getById(currentLoc.parent_id);
              currentLoc = parentResponse.data;
            } else break;
          } else if (currentLoc.location_type === 'state') {
            hierarchy.state_id = currentLoc.id.toString();
            if (currentLoc.parent_id) {
              const parentResponse = await locationsApi.getById(currentLoc.parent_id);
              currentLoc = parentResponse.data;
            } else break;
          } else if (currentLoc.location_type === 'country') {
            hierarchy.country_id = currentLoc.id.toString();
            break;
          } else {
            break;
          }
        }
        
        // Set hierarchy and fetch children for each level
        setLocationFormData(hierarchy);
        
        // Fetch children for each populated level
        if (hierarchy.country_id) {
          const statesResponse = await locationsApi.getChildren(parseInt(hierarchy.country_id));
          setStates(statesResponse.data || []);
        }
        if (hierarchy.state_id) {
          const districtsResponse = await locationsApi.getChildren(parseInt(hierarchy.state_id));
          setDistricts(districtsResponse.data || []);
        }
        if (hierarchy.district_id) {
          const areasResponse = await locationsApi.getChildren(parseInt(hierarchy.district_id));
          setAreas(areasResponse.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching location hierarchy:', error);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleLocationFormChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLocationFormData(prev => ({ ...prev, [name]: value }));
    
    // Update the final location_id based on the selected level
    const finalLocationId = value || locationFormData.area_id || locationFormData.district_id || locationFormData.state_id || locationFormData.country_id;
    setFormData(prev => ({ ...prev, location_id: finalLocationId }));
  };

  const handleSave = async () => {
    try {
      const updateData: any = {
        id: currentShop!.id,
        name: formData.name,
        description: formData.description,
        is_public: formData.is_public,
      };

      // Add location fields if provided - use the most specific location selected
      const finalLocationId = locationFormData.area_id || locationFormData.district_id || locationFormData.state_id || locationFormData.country_id;
      if (finalLocationId) {
        updateData.location_id = parseInt(finalLocationId);
      }
      if (formData.latitude) {
        updateData.latitude = parseFloat(formData.latitude);
      }
      if (formData.longitude) {
        updateData.longitude = parseFloat(formData.longitude);
      }

      // Add business information fields if provided
      if (formData.business_email) {
        updateData.business_email = formData.business_email;
      }
      if (formData.business_phone) {
        updateData.business_phone = formData.business_phone;
      }
      if (formData.business_address) {
        updateData.business_address = formData.business_address;
      }
      if (formData.business_website) {
        updateData.business_website = formData.business_website;
      }
      if (formData.business_hours) {
        updateData.business_hours = formData.business_hours;
      }

      await dispatch(updateShop(updateData)).unwrap();
      setIsEditing(false);
      toast.success('Shop updated successfully!');
    } catch (error) {
      console.error('Failed to update shop:', error);
      toast.error('Failed to update shop');
    }
  };

  const handleCancel = () => {
    if (currentShop) {
      setFormData({
        name: currentShop.name || '',
        description: currentShop.description || '',
        is_public: currentShop.is_public || false,
        location_id: currentShop.location_id?.toString() || '',
        latitude: currentShop.latitude?.toString() || '',
        longitude: currentShop.longitude?.toString() || '',
        business_email: currentShop.business_email || '',
        business_phone: currentShop.business_phone || '',
        business_address: currentShop.business_address || '',
        business_website: currentShop.business_website || '',
        business_hours: currentShop.business_hours || '',
      });
      // Reset location form data
      setLocationFormData({
        country_id: '',
        state_id: '',
        district_id: '',
        area_id: '',
      });
      // Re-fetch location hierarchy if shop has location
      if (currentShop.location_id) {
        fetchLocationHierarchy(currentShop.location_id);
      }
    }
    setIsEditing(false);
  };

  const handleToggleVisibility = async () => {
    try {
      await dispatch(updateShop({ 
        id: currentShop!.id, 
        is_public: !currentShop!.is_public 
      })).unwrap();
      
      // Refresh shop links if shop is now public
      if (!currentShop!.is_public) {
        const response = await shopsApi.getMyShopLink();
        if (response.data?.shop_url) {
          setServerLink(response.data.shop_url);
          const slug = currentShop!.slug || currentShop!.id.toString();
          const frontendUrl = `${FRONTEND_BASE_URL}/shop/${slug}`;
          setShopLink(frontendUrl);
        }
      } else {
        setServerLink(null);
        setShopLink(null);
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
    }
  };


  const handleViewAsBuyer = () => {
    if (shopLink) {
      window.open(shopLink, '_blank');
    } else if (currentShop?.slug) {
      window.open(`${FRONTEND_BASE_URL}/shop/${currentShop.slug}`, '_blank');
    } else if (currentShop?.id) {
      window.open(`${FRONTEND_BASE_URL}/shop/id/${currentShop.id}`, '_blank');
    }
  };

  // Handle logo file selection
  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle banner file selection
  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setBannerFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async () => {
    if (!logoFile || !currentShop) return;

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);

      await apiClient.post(`/shops/${currentShop.id}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Logo uploaded successfully!');
      setLogoFile(null);
      setLogoPreview(null);
      // Refresh shop data
      dispatch(fetchMyShop());
    } catch (error: any) {
      console.error('Failed to upload logo:', error);
      toast.error(error?.detail || 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Handle banner upload
  const handleBannerUpload = async () => {
    if (!bannerFile || !currentShop) return;

    setIsUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', bannerFile);

      await apiClient.post(`/shops/${currentShop.id}/upload-banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Banner uploaded successfully!');
      setBannerFile(null);
      setBannerPreview(null);
      // Refresh shop data
      dispatch(fetchMyShop());
    } catch (error: any) {
      console.error('Failed to upload banner:', error);
      toast.error(error?.detail || 'Failed to upload banner');
    } finally {
      setIsUploadingBanner(false);
    }
  };

  // Handle logo delete
  const handleLogoDelete = async () => {
    if (!currentShop?.logo_url) return;

    if (!window.confirm('Are you sure you want to delete the logo?')) return;

    try {
      await apiClient.delete(`/shops/${currentShop.id}/logo`);
      toast.success('Logo deleted successfully!');
      // Refresh shop data
      dispatch(fetchMyShop());
    } catch (error: any) {
      console.error('Failed to delete logo:', error);
      toast.error(error?.detail || 'Failed to delete logo');
    }
  };

  // Handle banner delete
  const handleBannerDelete = async () => {
    if (!currentShop?.banner_url) return;

    if (!window.confirm('Are you sure you want to delete the banner?')) return;

    try {
      await apiClient.delete(`/shops/${currentShop.id}/banner`);
      toast.success('Banner deleted successfully!');
      // Refresh shop data
      dispatch(fetchMyShop());
    } catch (error: any) {
      console.error('Failed to delete banner:', error);
      toast.error(error?.detail || 'Failed to delete banner');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading shop...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/seller')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Shop</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentShop) {
    return (
      <div>
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/seller')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">🏪</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Shop Found</h2>
            <p className="text-gray-600 mb-6">You don't have a shop yet. Create one to start selling!</p>
            <Button onClick={() => window.location.href = '/dashboard/seller/shop/create'}>
              <BuildingStorefrontIcon className="h-5 w-5 mr-2" />
              Create Shop
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/seller')}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Dashboard
        </button>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Shop Management</h1>
              <p className="mt-2 text-gray-600">Manage your shop settings and information</p>
            </div>
            <div className="flex space-x-3">
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)}>
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Edit Shop
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>
                    Save Changes
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Shop Status Alert */}
        {!currentShop.is_public && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md mb-6">
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 mr-2" />
              <div>
                <p className="font-medium">Shop Pending Approval</p>
                <p className="text-sm">Your shop is currently under review. It will be visible to customers once approved by admin.</p>
              </div>
            </div>
          </div>
        )}

        {/* Shop Information */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Shop Information</h2>
          
          <div className="space-y-6">
            {/* Shop Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter your shop name"
                />
              ) : (
                <p className="text-lg font-medium text-gray-900">{currentShop.name}</p>
              )}
            </div>

            {/* Shop Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Description
              </label>
              {isEditing ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Describe your shop and what you sell"
                />
              ) : (
                <p className="text-gray-700">{currentShop.description}</p>
              )}
            </div>

            {/* Shop Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Status
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  {currentShop.is_public ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <ClockIcon className="h-5 w-5 text-yellow-500 mr-2" />
                  )}
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    currentShop.is_public 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {currentShop.is_public ? 'Public' : 'Pending Approval'}
                  </span>
                </div>
                
                {currentShop.admin_rating && (
                  <div className="flex items-center">
                    <StarIcon className="h-5 w-5 text-yellow-400 mr-1" />
                    <span className="text-sm text-gray-600">
                      Admin Rating: {currentShop.admin_rating}/5
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Location Section */}
            {isEditing && (
              <div className="border-t pt-6 mt-6">
                <h3 className="text-md font-semibold text-gray-900 mb-4">Shop Location</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <select
                        name="country_id"
                        value={locationFormData.country_id}
                        onChange={handleLocationFormChange}
                        disabled={isLoadingLocations}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      >
                        <option value="">Select country</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State/Province
                      </label>
                      <select
                        name="state_id"
                        value={locationFormData.state_id}
                        onChange={handleLocationFormChange}
                        disabled={!locationFormData.country_id || isLoadingLocations}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      >
                        <option value="">Select state</option>
                        {states.map((state) => (
                          <option key={state.id} value={state.id}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        District
                      </label>
                      <select
                        name="district_id"
                        value={locationFormData.district_id}
                        onChange={handleLocationFormChange}
                        disabled={!locationFormData.state_id || isLoadingLocations}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      >
                        <option value="">Select district</option>
                        {districts.map((district) => (
                          <option key={district.id} value={district.id}>
                            {district.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Area
                      </label>
                      <select
                        name="area_id"
                        value={locationFormData.area_id}
                        onChange={handleLocationFormChange}
                        disabled={!locationFormData.district_id || isLoadingLocations}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                      >
                        <option value="">Select area</option>
                        {areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    Select the location where your shop is located. The most specific location selected will be saved.
                  </p>

                  {/* GPS Coordinates */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        GPS Coordinates (Optional)
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                              (position) => {
                                setFormData(prev => ({
                                  ...prev,
                                  latitude: position.coords.latitude.toString(),
                                  longitude: position.coords.longitude.toString()
                                }));
                                toast.success('Location captured!');
                              },
                              (error) => {
                                toast.error('Could not get your location');
                              }
                            );
                          } else {
                            toast.error('Geolocation is not supported');
                          }
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        Use My Location
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Latitude"
                        name="latitude"
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => handleInputChange('latitude', e.target.value)}
                        placeholder="e.g., 23.8103"
                      />
                      <Input
                        label="Longitude"
                        name="longitude"
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => handleInputChange('longitude', e.target.value)}
                        placeholder="e.g., 90.4125"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Shop Branding */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Shop Branding</h2>

          <div className="space-y-8">
            {/* Banner Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Shop Banner
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended size: 1920x400px (aspect ratio 4.8:1)
                  </p>
                </div>
                {currentShop.banner_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBannerDelete}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Delete Banner
                  </Button>
                )}
              </div>

              {/* Current Banner or Preview */}
              {(bannerPreview || currentShop.banner_url) && (
                <div className="mb-4">
                  <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-300">
                    <img
                      src={bannerPreview || getImageUrl(currentShop.banner_url)}
                      alt="Shop Banner"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="400"><rect fill="%23e5e7eb" width="100%" height="100%"/><text fill="%239ca3af" font-family="sans-serif" font-size="18" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Banner</text></svg>';
                      }}
                    />
                    {bannerPreview && (
                      <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                        Preview
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-400 transition-colors">
                <div className="flex flex-col items-center">
                  <div className="mb-4">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <label className="cursor-pointer">
                    <span className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                      Choose Banner Image
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerFileChange}
                      className="hidden"
                    />
                  </label>
                  <p className="mt-2 text-xs text-gray-500">PNG, JPG up to 5MB</p>
                  {bannerFile && (
                    <div className="mt-4 flex items-center gap-2">
                      <span className="text-sm text-gray-700">{bannerFile.name}</span>
                      <Button
                        size="sm"
                        onClick={handleBannerUpload}
                        disabled={isUploadingBanner}
                      >
                        {isUploadingBanner ? 'Uploading...' : 'Upload Banner'}
                      </Button>
                      <button
                        onClick={() => {
                          setBannerFile(null);
                          setBannerPreview(null);
                        }}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Logo Section */}
            <div className="border-t pt-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Shop Logo
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended size: 400x400px (square)
                  </p>
                </div>
                {currentShop.logo_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogoDelete}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Delete Logo
                  </Button>
                )}
              </div>

              {/* Current Logo or Preview */}
              {(logoPreview || currentShop.logo_url) && (
                <div className="mb-4">
                  <div className="relative w-48 h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-300 mx-auto">
                    <img
                      src={logoPreview || getImageUrl(currentShop.logo_url)}
                      alt="Shop Logo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect fill="%23e5e7eb" width="100%" height="100%"/><text fill="%239ca3af" font-family="sans-serif" font-size="18" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Logo</text></svg>';
                      }}
                    />
                    {logoPreview && (
                      <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                        Preview
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-400 transition-colors">
                <div className="flex flex-col items-center">
                  <div className="mb-4">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <label className="cursor-pointer">
                    <span className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                      Choose Logo Image
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="hidden"
                    />
                  </label>
                  <p className="mt-2 text-xs text-gray-500">PNG, JPG up to 5MB</p>
                  {logoFile && (
                    <div className="mt-4 flex items-center gap-2">
                      <span className="text-sm text-gray-700">{logoFile.name}</span>
                      <Button
                        size="sm"
                        onClick={handleLogoUpload}
                        disabled={isUploadingLogo}
                      >
                        {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      <button
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview(null);
                        }}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Business Information</h2>

          <div className="space-y-6">
            {/* Business Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.business_email}
                  onChange={(e) => handleInputChange('business_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="contact@yourbusiness.com"
                />
              ) : (
                <p className="text-gray-700">{currentShop.business_email || 'Not provided'}</p>
              )}
            </div>

            {/* Business Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Phone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={formData.business_phone}
                  onChange={(e) => handleInputChange('business_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="+1 (555) 123-4567"
                />
              ) : (
                <p className="text-gray-700">{currentShop.business_phone || 'Not provided'}</p>
              )}
            </div>

            {/* Business Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Address
              </label>
              {isEditing ? (
                <textarea
                  value={formData.business_address}
                  onChange={(e) => handleInputChange('business_address', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="123 Main Street, City, State, ZIP Code"
                />
              ) : (
                <p className="text-gray-700 whitespace-pre-wrap">{currentShop.business_address || 'Not provided'}</p>
              )}
            </div>

            {/* Business Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Website
              </label>
              {isEditing ? (
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">https://</span>
                  </div>
                  <input
                    type="url"
                    value={formData.business_website}
                    onChange={(e) => handleInputChange('business_website', e.target.value)}
                    className="w-full pl-16 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="www.yourbusiness.com"
                  />
                </div>
              ) : (
                currentShop.business_website ? (
                  <a
                    href={currentShop.business_website.startsWith('http') ? currentShop.business_website : `https://${currentShop.business_website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    {currentShop.business_website}
                  </a>
                ) : (
                  <p className="text-gray-700">Not provided</p>
                )
              )}
            </div>

            {/* Business Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Hours
              </label>
              {isEditing ? (
                <textarea
                  value={formData.business_hours}
                  onChange={(e) => handleInputChange('business_hours', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Mon-Fri: 9:00 AM - 6:00 PM&#10;Sat: 10:00 AM - 4:00 PM&#10;Sun: Closed"
                />
              ) : (
                <p className="text-gray-700 whitespace-pre-wrap">{currentShop.business_hours || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Shop Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <BuildingStorefrontIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Shop ID</p>
                <p className="text-2xl font-bold text-gray-900">#{currentShop.id}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Visibility</p>
                <p className="text-2xl font-bold text-gray-900">
                  {currentShop.is_public ? 'Public' : 'Private'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <StarIcon className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Admin Rating</p>
                <p className="text-2xl font-bold text-gray-900">
                  {currentShop.admin_rating ? `${currentShop.admin_rating}/5` : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Shop URL Section - F-commerce */}
        {currentShop.is_public && (
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg shadow-sm border border-primary-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <LinkIcon className="h-5 w-5 mr-2 text-primary-600" />
              Your Shop Links (F-commerce)
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Share the <strong>Shop Link</strong> on Facebook, Instagram, or any social media platform. This is what buyers will see.
            </p>
            {isLoadingLink ? (
              <div className="flex items-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                Loading shop links...
              </div>
            ) : shopLink ? (
              <div className="space-y-4">
                {/* Shop Link (Frontend - For Buyers) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shop Link (For Buyers) <span className="text-green-600">★ Use This</span>
                  </label>
                  <div className="flex items-center gap-2 bg-white rounded-lg border-2 border-green-200 p-3">
                    <input
                      type="text"
                      value={shopLink}
                      readOnly
                      className="flex-1 text-sm text-gray-700 bg-transparent border-none focus:outline-none"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shopLink).then(() => {
                          toast.success('Shop link copied to clipboard!');
                        }).catch(() => {
                          toast.error('Failed to copy link');
                        });
                      }}
                      className="p-2 text-gray-500 hover:text-green-600 transition-colors"
                      title="Copy shop link"
                    >
                      <ClipboardDocumentIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">This is the link buyers will visit to see your shop</p>
                </div>

                {/* Server Link (API - For Reference) */}
                {serverLink && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Server Link (API - For Reference Only)
                    </label>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 p-3">
                      <input
                        type="text"
                        value={serverLink}
                        readOnly
                        className="flex-1 text-sm text-gray-500 bg-transparent border-none focus:outline-none"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(serverLink).then(() => {
                            toast.success('Server link copied to clipboard!');
                          }).catch(() => {
                            toast.error('Failed to copy link');
                          });
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Copy server link"
                      >
                        <ClipboardDocumentIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Backend API link (for technical reference)</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleViewAsBuyer}
                    className="flex items-center"
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
                    View as Buyer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (shopLink) {
                        navigator.clipboard.writeText(shopLink).then(() => {
                          toast.success('Shop link copied to clipboard!');
                        }).catch(() => {
                          toast.error('Failed to copy link');
                        });
                      }
                    }}
                    className="flex items-center"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5 mr-2" />
                    Copy Shop Link
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Shop link will be available once your shop is approved.</p>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/seller/products')}
              className="justify-start"
            >
              <PencilIcon className="h-5 w-5 mr-2" />
              Manage Products
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/seller/orders')}
              className="justify-start"
            >
              <EyeIcon className="h-5 w-5 mr-2" />
              View Orders
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/seller/analytics')}
              className="justify-start"
            >
              <StarIcon className="h-5 w-5 mr-2" />
              View Analytics
            </Button>
            <Button
              variant="outline"
              onClick={handleToggleVisibility}
              className="justify-start"
            >
              {currentShop.is_public ? (
                <>
                  <EyeSlashIcon className="h-5 w-5 mr-2" />
                  Make Private
                </>
              ) : (
                <>
                  <EyeIcon className="h-5 w-5 mr-2" />
                  Make Public
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopManagementPage;
